import '@app/App.css';
import { Button } from "@comps/ui/button";
import { DialogDescription, DialogHeader, DialogTitle } from "@comps/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@comps/ui/form";
import { Input } from "@comps/ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@comps/ui/tooltip";
import { zodResolver } from "@hookform/resolvers/zod";
import { getWsButtonStyle, TW_AppDialogButton, TW_ButtonList, TW_ErrorMessage, useHover } from "@utils/twClasses";
import { MAX_FOLDER_DEPTH } from "@dto/preferences";
import { createFolder, exceedsFolderDepth, folderExists } from "@utils/viewFolders";
import { HelpCircle } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { translate, translateHtml } from "src/i18n/i18n";
import { useModelStore } from "src/store";
import { z } from "zod";
import { useViewFolders } from "./useViewFolders";

/**
 * Header of the folder creation dialogs: title plus a '?' icon whose tooltip
 * documents the "A/B" mkdir-p behavior of the name field. The description
 * stays in the DOM, hidden, for accessibility.
 */
export function ViewFolderCreationHeader() {
    const { t } = useTranslation();
    return (
        <DialogHeader>
            <div className="flex flex-row items-center gap-2">
                <DialogTitle>{t("folder.dialog.new.title")}</DialogTitle>
                <TooltipProvider>
                    <Tooltip delayDuration={100}>
                        <TooltipTrigger asChild>
                            <HelpCircle className="w-4 h-4 min-w-4 min-h-4 text-gray-400 cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-80">
                            {translateHtml(t, "folder.dialog.new.description", [MAX_FOLDER_DEPTH])}
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </div>
            <DialogDescription className="hidden">{t("folder.dialog.new.description")}</DialogDescription>
        </DialogHeader>
    )
}

interface ViewFolderFormProps {
    objectKey: string;
    // null = create at the root; a folder path = create inside it
    parentPath: string | null;
    onDone: (path: string) => void;
    onCancel: (event: React.MouseEvent<HTMLButtonElement> | undefined) => void;
}

/**
 * Creation form of a personal view folder (used from the object menu for the
 * root and from a folder menu for a subfolder). A name containing the
 * separator creates the intermediate levels.
 */
function ViewFolderForm({ objectKey, parentPath, onDone, onCancel }: ViewFolderFormProps) {

    const { t } = useTranslation();
    const { prefs, saveFolders } = useViewFolders(objectKey);
    const [error, setError] = useState<string | null>(null);
    const wsColor = useModelStore(state => state.wsColor);
    const wsDarkColor = useModelStore(state => state.wsDarkColor);
    const cancelHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));
    const okHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));

    const formSchema = z.object({
        folderName: z.string().min(1, {
            message: t("folder.dialog.error.name.missing"),
        }),
    })

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            folderName: t("folder.dialog.name.default.value"),
        },
    })

    function onSubmit(values: z.infer<typeof formSchema>) {
        // An already existing folder keeps the dialog open with an explicit
        // message instead of silently reusing it
        if (folderExists(prefs, parentPath, values.folderName)) {
            setError(t("folder.dialog.error.name.exists"));
            return;
        }
        const created = createFolder(prefs, parentPath, values.folderName);
        if (!created) {
            if (exceedsFolderDepth(parentPath, values.folderName)) {
                setError(translate(t, "folder.dialog.error.name.tooDeep", [MAX_FOLDER_DEPTH]));
            } else {
                setError(t("folder.dialog.error.name.invalid"));
            }
            return;
        }
        saveFolders(created.data).then(success => {
            if (success) {
                setError(null);
                onDone(created.path);
            }
        })
    }

    function onEnterKeyPress(event: React.KeyboardEvent) {
        if (event.key === "Enter") {
            event.preventDefault();
            event.stopPropagation();
            form.handleSubmit(onSubmit)();
        }
    }

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                <FormField
                    control={form.control}
                    name="folderName"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>{t("folder.dialog.name.label")}</FormLabel>
                            <FormControl>
                                <Input placeholder={t("folder.dialog.name.placeholder")} {...field} onKeyDown={onEnterKeyPress} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                {error && <div className={TW_ErrorMessage}>{error}</div>}
                {!error && <div className={TW_ErrorMessage}></div>}
                <div className={TW_ButtonList}>
                    <Button className={TW_AppDialogButton} onClick={onCancel} {...cancelHover}>{t("general.button.cancel.text")}</Button>
                    <Button className={TW_AppDialogButton} type="submit" {...okHover}>{t("general.button.ok.text")}</Button>
                </div>
            </form>
        </Form>
    )
}

export default ViewFolderForm;
